/**
 * First-run onboarding (/onboarding), a short stepper: (1) pick an existing project (optionally renaming it) or
 * create the first one, and pick its wiki template; (2) connect a coding agent; (3) choose the compile mode.
 * Skippable at any step. Finishing or skipping writes the `onboarding` preference so it shows once.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Check, FolderKanban, FolderPlus } from 'lucide-react'
import {
  createProjectSchema,
  DEFAULT_WIKI_TEMPLATE,
  updateProjectSchema,
  type CompileMode,
  type CreateProjectInput,
  type ProjectSummary,
  type WikiTemplateId,
} from '@agentwiki/shared'
import { useAuth } from '../hooks/use-auth'
import { apiErrorCode, useCreateProject, useProjects, useUpdateProject } from '../hooks/use-projects'
import { useFinishOnboarding, useOnboardingStatus } from '../hooks/use-onboarding'
import { useCompileMode, useSetCompileMode } from '../hooks/use-compile-mode'
import { useAppStore } from '../stores/app-store'
import { useT, type TranslationKey } from '../i18n'
import { cn } from '../lib/utils'
import { AgentWikiLogo } from '../components/brand/agentwiki-logo'
import { LanguageSwitcher, ThemeSwitcher } from '../components/shell/preference-switchers'
import { projectPath } from '../components/shell/nav-config'
import { ConnectAgentStep } from '../components/onboarding/connect-agent-step'
import { CompileModePicker, WikiTemplatePicker } from '../components/setup/setup-pickers'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, hintClass, inputClass, labelClass } from '../components/ui/styles'

type Choice = 'existing' | 'new'
type Step = 'project' | 'agent' | 'compile'

export const ONBOARDING_STEPS: readonly Step[] = ['project', 'agent', 'compile']
const STEP_LABEL: Record<Step, TranslationKey> = {
  project: 'onboarding.step.project',
  agent: 'onboarding.step.agent',
  compile: 'onboarding.step.compile',
}
const STEP_HEADING: Record<Exclude<Step, 'project'>, TranslationKey> = {
  agent: 'onboarding.agent.title',
  compile: 'onboarding.compile.title',
}

function Stepper({ step }: { step: Step }) {
  const t = useT()
  const current = ONBOARDING_STEPS.indexOf(step)
  return (
    <nav aria-label={t('onboarding.steps')}>
      <p className="sr-only">{t('onboarding.stepOf', { current: current + 1, total: ONBOARDING_STEPS.length })}</p>
      <ol className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        {ONBOARDING_STEPS.map((s, i) => (
          <li key={s} aria-current={i === current ? 'step' : undefined} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold',
                i < current ? 'border-accent bg-accent text-accent-fg' : i === current ? 'border-accent text-accent-text' : 'border-border text-fg-subtle',
              )}
            >
              {i < current ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn(i === current ? 'font-medium text-fg' : 'text-fg-muted')}>{t(STEP_LABEL[s])}</span>
            {i < ONBOARDING_STEPS.length - 1 && <span aria-hidden="true" className="h-px w-6 bg-border" />}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function OnboardingPage() {
  const t = useT()
  const navigate = useNavigate()
  const id = useId()
  const { user } = useAuth()
  const status = useOnboardingStatus()
  const { data: projects } = useProjects()
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()
  const finish = useFinishOnboarding()
  const savedCompileMode = useCompileMode()
  const setCompileModePref = useSetCompileMode()
  const setLastProjectSlug = useAppStore((s) => s.setLastProjectSlug)

  const [step, setStep] = useState<Step>('project')
  const [choice, setChoice] = useState<Choice>('existing')
  const [selected, setSelected] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [repoUrl, setRepoUrl] = useState('')
  const [wikiTemplate, setWikiTemplate] = useState<WikiTemplateId>(DEFAULT_WIKI_TEMPLATE)
  const [compileMode, setCompileMode] = useState<CompileMode | null>(null)
  const [project, setProject] = useState<Pick<ProjectSummary, 'slug' | 'name'> | null>(null)
  // Project created in step 1; going back and saving again updates it instead of creating a second one
  const [createdSlug, setCreatedSlug] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Once shown, the flow runs to the end: creating a project in step 1 would otherwise end the gate early
  const [entered, setEntered] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const firstRender = useRef(true)

  const selectedProject = projects?.find((p) => p.slug === selected)

  useEffect(() => {
    if (status === 'needed') setEntered(true)
  }, [status])

  // Pre-select the default project and show its current name / description
  useEffect(() => {
    if (!projects?.length || selected) return
    const initial = projects.find((p) => p.isDefault) ?? projects[0]
    setSelected(initial.slug)
  }, [projects, selected])

  // Reset the form when the choice or the picked project changes (keyed by id: refetches return new objects)
  const selectedId = selectedProject?.id
  useEffect(() => {
    const picked = choice === 'existing' ? projects?.find((p) => p.id === selectedId) : undefined
    setName(picked?.name ?? '')
    setDescription(picked?.description ?? '')
    setRepoUrl('')
    setWikiTemplate(picked?.wikiTemplate ?? DEFAULT_WIKI_TEMPLATE)
    setError(null)
  }, [choice, selectedId]) // projects is read for the initial values only

  // Move focus to the new step's heading so screen readers announce it
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    headingRef.current?.focus()
  }, [step])

  // Someone who already finished (or reached this URL by hand) goes straight to the app
  if (status === 'done' && !entered && !busy && !finish.isSuccess) return <Navigate to="/p" replace />

  const failure = (err: unknown) => t('onboarding.failed', { message: err instanceof Error ? err.message : String(err) })

  const saveProject = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (choice === 'existing') {
        if (!selectedProject) return
        const parsedUpdate = updateProjectSchema.safeParse({ name, description: description.trim() || null, wikiTemplate })
        if (!parsedUpdate.success) {
          setError(parsedUpdate.error.issues[0]?.path[0] === 'name' ? t('project.create.nameRequired') : t('project.create.failed', { message: parsedUpdate.error.issues[0]?.message ?? '' }))
          return
        }
        const input = parsedUpdate.data
        const changed = input.name !== selectedProject.name
          || (input.description ?? null) !== (selectedProject.description ?? null)
          || input.wikiTemplate !== selectedProject.wikiTemplate
        const saved = changed ? await updateProject.mutateAsync({ ref: selectedProject.slug, input }) : selectedProject
        setProject({ slug: saved.slug, name: saved.name })
      } else {
        const raw: CreateProjectInput = { name, wikiTemplate }
        if (description.trim()) raw.description = description
        if (repoUrl.trim()) raw.repoUrl = repoUrl
        const parsed = createProjectSchema.safeParse(raw)
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.path[0] === 'name' ? t('project.create.nameRequired') : t('project.create.failed', { message: parsed.error.issues[0]?.message ?? '' }))
          return
        }
        const created = createdSlug
          ? await updateProject.mutateAsync({
            ref: createdSlug,
            input: { name: parsed.data.name, description: parsed.data.description ?? null, repoUrl: parsed.data.repoUrl ?? null, wikiTemplate },
          })
          : await createProject.mutateAsync(parsed.data)
        setCreatedSlug(created.slug)
        setProject({ slug: created.slug, name: created.name })
      }
      setStep('agent')
    } catch (err) {
      setError(apiErrorCode(err) === 'PROJECT_SLUG_TAKEN' ? t('project.create.nameTaken') : failure(err))
    } finally {
      setBusy(false)
    }
  }

  const complete = async () => {
    if (!project) return
    setError(null)
    setBusy(true)
    try {
      try {
        await setCompileModePref.mutateAsync(compileMode ?? savedCompileMode)
      } catch (err) {
        // The compile mode is a preference that can be changed in Settings; never block finishing on it
        console.error('Could not save the compile mode:', err)
      }
      await finish.mutateAsync('completed')
      setLastProjectSlug(project.slug)
      navigate(projectPath(project.slug), { replace: true })
    } catch (err) {
      setError(failure(err))
      setBusy(false)
    }
  }

  const skip = async () => {
    setBusy(true)
    try {
      await finish.mutateAsync('skipped')
      if (project) setLastProjectSlug(project.slug)
      navigate(project ? projectPath(project.slug) : '/p', { replace: true })
    } catch (err) {
      setError(failure(err))
      setBusy(false)
    }
  }

  const back = () => {
    setError(null)
    setStep(ONBOARDING_STEPS[Math.max(0, ONBOARDING_STEPS.indexOf(step) - 1)])
  }

  const optionClass = (active: boolean) =>
    cn(
      cardClass,
      'flex min-h-11 cursor-pointer items-start gap-3 p-4 text-left has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent',
      active ? 'border-accent ring-1 ring-accent' : 'hover:border-border-strong',
    )

  const skipButton = (
    <button type="button" onClick={() => void skip()} disabled={busy} className="min-h-9 cursor-pointer text-sm text-fg-subtle hover:text-fg disabled:opacity-60">
      {t('onboarding.skip')}
    </button>
  )

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-surface font-sans text-fg antialiased">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[480px] w-[640px] max-w-full -translate-x-1/2 rounded-full bg-accent-soft blur-[120px]" aria-hidden="true" />

      <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 px-4 py-4">
        <AgentWikiLogo />
        <div className="flex flex-wrap gap-2">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      </div>

      <main className="relative z-10 flex flex-1 justify-center px-4 pb-16 pt-4 md:pt-10">
        <div className="w-full max-w-xl space-y-6">
          {step === 'project' ? (
            <div className="space-y-2">
              <h1 ref={headingRef} tabIndex={-1} className="font-display text-2xl font-semibold tracking-tight outline-none md:text-3xl">
                {user?.name ? t('onboarding.welcomeNamed', { name: user.name }) : t('onboarding.welcome')}
              </h1>
              <p className="text-sm leading-relaxed text-fg-muted md:text-base">{t('onboarding.intro')}</p>
            </div>
          ) : (
            <h1 ref={headingRef} tabIndex={-1} className="font-display text-2xl font-semibold tracking-tight outline-none md:text-3xl">
              {t(STEP_HEADING[step])}
            </h1>
          )}

          <Stepper step={step} />

          {status === 'loading' || !projects ? (
            <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
          ) : step === 'project' ? (
            <form onSubmit={saveProject} className="space-y-5" noValidate>
              <fieldset className="grid gap-3 sm:grid-cols-2">
                <legend className="sr-only">{t('onboarding.choose')}</legend>
                <label className={optionClass(choice === 'existing')}>
                  <input type="radio" name="choice" value="existing" checked={choice === 'existing'} onChange={() => setChoice('existing')} className="sr-only" />
                  <FolderKanban className="mt-0.5 h-5 w-5 shrink-0 text-accent-text" aria-hidden="true" />
                  <span>
                    <span className="block text-sm font-semibold">{t('onboarding.useExisting')}</span>
                    <span className="mt-0.5 block text-sm text-fg-muted">{t('onboarding.useExistingHint')}</span>
                  </span>
                </label>
                <label className={optionClass(choice === 'new')}>
                  <input type="radio" name="choice" value="new" checked={choice === 'new'} onChange={() => setChoice('new')} className="sr-only" />
                  <FolderPlus className="mt-0.5 h-5 w-5 shrink-0 text-accent-text" aria-hidden="true" />
                  <span>
                    <span className="block text-sm font-semibold">{t('onboarding.createNew')}</span>
                    <span className="mt-0.5 block text-sm text-fg-muted">{t('onboarding.createNewHint')}</span>
                  </span>
                </label>
              </fieldset>

              <div className={cn(cardClass, 'space-y-4 p-4 md:p-5')}>
                {choice === 'existing' && projects.length > 1 && (
                  <div className="space-y-1.5">
                    <label htmlFor={`${id}-project`} className={labelClass}>
                      {t('nav.project')}
                    </label>
                    <select id={`${id}-project`} value={selected} onChange={(e) => setSelected(e.target.value)} className={inputClass}>
                      {projects.map((p) => (
                        <option key={p.id} value={p.slug}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <label htmlFor={`${id}-name`} className={labelClass}>
                    {t('onboarding.projectName')}
                  </label>
                  <input
                    id={`${id}-name`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                    required
                    placeholder={t('project.create.namePlaceholder')}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor={`${id}-description`} className={labelClass}>
                    {t('project.create.description')}
                  </label>
                  <textarea
                    id={`${id}-description`}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder={t('onboarding.descriptionPlaceholder')}
                    className={cn(inputClass, 'resize-y')}
                  />
                </div>
                {choice === 'new' && (
                  <div className="space-y-1.5">
                    <label htmlFor={`${id}-repo`} className={labelClass}>
                      {t('project.create.repoUrl')}
                    </label>
                    <input
                      id={`${id}-repo`}
                      type="url"
                      value={repoUrl}
                      onChange={(e) => setRepoUrl(e.target.value)}
                      maxLength={500}
                      placeholder="https://github.com/org/repo"
                      className={inputClass}
                    />
                  </div>
                )}
                <WikiTemplatePicker value={wikiTemplate} onChange={setWikiTemplate} />
                <p className={hintClass}>{t('onboarding.laterHint')}</p>
              </div>

              {error && (
                <p role="alert" className={errorTextClass}>
                  {error}
                </p>
              )}

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                {skipButton}
                <button type="submit" disabled={busy} className={buttonPrimaryClass}>
                  {busy ? t('onboarding.working') : choice === 'existing' ? t('onboarding.continue') : t('project.create.submit')}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-5">
              {step === 'agent' && project && <ConnectAgentStep project={project.slug} projectName={project.name} />}
              {step === 'compile' && (
                <div className="space-y-3">
                  <p className="text-sm text-fg-muted">{t('onboarding.compile.intro')}</p>
                  <CompileModePicker value={compileMode ?? savedCompileMode} onChange={setCompileMode} hint={t('compileMode.hint')} />
                </div>
              )}

              {error && (
                <p role="alert" className={errorTextClass}>
                  {error}
                </p>
              )}

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                {skipButton}
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  <button type="button" onClick={back} disabled={busy} className={buttonSecondaryClass}>
                    {t('onboarding.back')}
                  </button>
                  {step === 'agent' ? (
                    <button type="button" onClick={() => setStep('compile')} className={buttonPrimaryClass}>
                      {t('onboarding.next')}
                    </button>
                  ) : (
                    <button type="button" onClick={() => void complete()} disabled={busy} className={buttonPrimaryClass}>
                      {busy ? t('onboarding.working') : t('onboarding.finish')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
